import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  FolderOpen,
  MoreVertical,
  Trash2,
  Edit3,
  Share2,
  Archive,
  Clock,
  Box,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useProjects } from '@/hooks/useProjects'
import { formatDistanceToNow } from '@/lib/utils'
import CreateProjectModal from './CreateProjectModal'
import type { Project } from '@/lib/database.types'

interface ProjectsViewProps {
  onSelectProject: (project: Project) => void
}

const PROJECT_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
]

export default function ProjectsView({ onSelectProject }: ProjectsViewProps) {
  const { projects, unassignedCount, isLoading, createProject, deleteProject, isCreating } = useProjects()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [menuOpen, setMenuOpen] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)

  const handleCreateProject = async (data: { name: string; description: string; color: string }) => {
    setCreateError(null)
    try {
      await createProject({
        name: data.name,
        description: data.description || null,
        color: data.color,
        status: 'active',
        tags: [],
        is_public: false,
        shared_with: [],
        metadata: {},
        thumbnail_url: null,
      })
      setShowCreateModal(false)
    } catch (err: any) {
      console.error('Failed to create project:', err)
      setCreateError(err?.message || 'Failed to create project. Please try again.')
    }
  }

  const handleCloseModal = () => {
    setShowCreateModal(false)
    setCreateError(null)
  }

  const handleDeleteProject = async (id: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      await deleteProject(id)
    }
    setMenuOpen(null)
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Projects</h1>
            <p className="text-surface-400 mt-1">
              Organize your 3D designs into projects
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4" />
            New Project
          </Button>
        </div>

        {/* Unassigned Designs Notice */}
        {unassignedCount > 0 && (
          <Card className="p-4 border-amber-500/30 bg-amber-500/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Box className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-amber-400">
                  {unassignedCount} unassigned design{unassignedCount === 1 ? '' : 's'}
                </p>
                <p className="text-xs text-surface-400">
                  These designs aren't in any project. View them on the Dashboard.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-800 flex items-center justify-center">
              <FolderOpen className="w-8 h-8 text-surface-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No projects yet</h3>
            <p className="text-surface-400 mb-6 max-w-md mx-auto">
              Create your first project to start organizing your 3D designs
            </p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4" />
              Create First Project
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout">
              {projects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className="group cursor-pointer hover:border-surface-600 transition-all"
                    onClick={() => onSelectProject(project)}
                  >
                    {/* Color Bar */}
                    <div
                      className="h-2 rounded-t-xl -mx-px -mt-px"
                      style={{ backgroundColor: project.color || '#6366f1' }}
                    />
                    
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${project.color || '#6366f1'}20` }}
                          >
                            <FolderOpen
                              className="w-5 h-5"
                              style={{ color: project.color || '#6366f1' }}
                            />
                          </div>
                          <div>
                            <h3 className="font-medium text-white group-hover:text-primary-400 transition-colors">
                              {project.name}
                            </h3>
                            <p className="text-xs text-surface-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDistanceToNow(project.updated_at)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Menu */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuOpen(menuOpen === project.id ? null : project.id)
                            }}
                            className="p-1.5 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          
                          {menuOpen === project.id && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="absolute right-0 top-8 w-40 bg-surface-800 border border-surface-700 rounded-lg shadow-xl z-10 overflow-hidden"
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setMenuOpen(null)
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-surface-300 hover:bg-surface-700 flex items-center gap-2"
                              >
                                <Edit3 className="w-4 h-4" />
                                Rename
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setMenuOpen(null)
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-surface-300 hover:bg-surface-700 flex items-center gap-2"
                              >
                                <Share2 className="w-4 h-4" />
                                Share
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setMenuOpen(null)
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-surface-300 hover:bg-surface-700 flex items-center gap-2"
                              >
                                <Archive className="w-4 h-4" />
                                Archive
                              </button>
                              <div className="border-t border-surface-700" />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteProject(project.id)
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </motion.div>
                          )}
                        </div>
                      </div>
                      
                      {project.description && (
                        <p className="text-sm text-surface-400 line-clamp-2 mb-3">
                          {project.description}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-surface-500">
                        <span className="flex items-center gap-1">
                          <Box className="w-3 h-3" />
                          {(project as any).design_count ?? 0} design{(project as any).design_count === 1 ? '' : 's'}
                        </span>
                        {project.is_public && (
                          <span className="px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-400">
                            Public
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={handleCloseModal}
        onSubmit={handleCreateProject}
        isLoading={isCreating}
        colors={PROJECT_COLORS}
        error={createError}
      />
    </div>
  )
}
